<script setup>
import { computed } from "vue";
import dayjs from "dayjs";
import {
  BASE_TIMELINE_MINUTES,
  buildTimelineBarStyle,
  buildTimelineHours,
  getTimelineEndMinutes,
  parseTimeToMinutes,
} from "@/utils/resourceTimeline";

const props = defineProps({
  entries: { type: Object, required: true },
});

const RESOURCE_TONE_CLASSES = [
  "planner-bar--emerald",
  "planner-bar--rose",
  "planner-bar--violet",
  "planner-bar--cyan",
  "planner-bar--amber",
  "planner-bar--fuchsia",
];

const slotGroups = computed(() =>
  Array.isArray(props.entries?.dates)
    ? props.entries.dates
        .filter((entry) => entry?.date)
        .map((entry, index) => ({
          key: entry.id ?? `${entry.date}-${entry.start_time ?? ""}-${entry.finish_time ?? ""}-${index}`,
          date: entry.date,
          startTime: entry.start_time ?? null,
          finishTime: entry.finish_time ?? null,
          resources: Array.isArray(entry.resources) ? entry.resources : [],
        }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    : [],
);

const timelineEndMinutes = computed(() => getTimelineEndMinutes(slotGroups.value));
const timelineHours = computed(() => buildTimelineHours(timelineEndMinutes.value));
const timelineStyle = computed(() => ({
  "--planner-hour-count": timelineHours.value.length,
}));

const getTimelineBarStyle = (entry) =>
  buildTimelineBarStyle(entry, timelineEndMinutes.value);

const getTimelineToneClass = (resource) => {
  const toneSeed = Number(resource?.product_id ?? resource?.id ?? 0);
  return RESOURCE_TONE_CLASSES[
    Math.abs(toneSeed) % RESOURCE_TONE_CLASSES.length
  ];
};

const getResourceQuantity = (resource) => {
  const quantity = Number.parseInt(resource?.quantity, 10);
  return quantity > 0 ? quantity : 1;
};

const getResourceLabel = (resource) =>
  resource?.product?.name ??
  resource?.name ??
  `Unit ${resource?.product_id ?? ""}`;

const getTimelineRowsForResources = (resources) =>
  Array.isArray(resources) && resources.length
    ? Array.from(
        resources.reduce((groups, resource, index) => {
          const key = String(
            resource?.product_id ?? resource?.id ?? `resource-${index}`,
          );
          if (!groups.has(key)) {
            groups.set(key, {
              key,
              resource,
              quantities: [],
            });
          }

          const group = groups.get(key);
          group.quantities.push(getResourceQuantity(resource));
          return groups;
        }, new Map()).values(),
      ).map((group) => ({
        ...group,
        displayQuantity: Math.max(...group.quantities),
      }))
    : [];

const getTimelineResourceLabel = (group) =>
  `${group.displayQuantity} x ${getResourceLabel(group.resource)}`;

const getTimelineResourceMeta = (group) => {
  const note = group?.resource?.notes ?? group?.resource?.description ?? "";
  return note ? String(note) : "Scheduled resource";
};

const getDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const start = parseTimeToMinutes(startTime);
  let finish = parseTimeToMinutes(endTime);

  if (finish < start) {
    finish += BASE_TIMELINE_MINUTES;
  }

  return Math.max(0, Math.round((finish - start) / 60));
};
</script>

<template>
  <div class="planner-shell" :style="timelineStyle">
    <div class="planner-header-row">
      <div class="planner-date-header">Date</div>
      <div class="planner-hours-header">
        <div
          v-for="hour in timelineHours"
          :key="`hour-${hour.index}`"
          class="planner-hour-cell"
        >
          {{ hour.label }}
        </div>
      </div>
    </div>

    <div v-if="slotGroups.length" class="planner-body">
      <div
        v-for="slotGroup in slotGroups"
        :key="`planner-${slotGroup.key}`"
        class="planner-entry-row"
      >
        <div class="planner-date-cell">
          <div class="planner-date-title">
            {{ dayjs(slotGroup.date).format("DD MMM YYYY") }}
          </div>
          <div class="planner-date-subtitle">
            {{ dayjs(slotGroup.date).format("dddd") }}
          </div>
          <div class="planner-date-meta">
            {{ slotGroup.startTime?.slice(0, 5) }} -
            {{ slotGroup.finishTime?.slice(0, 5) }}
          </div>
          <div class="planner-date-meta">
            {{ getDuration(slotGroup.startTime, slotGroup.finishTime) }} hrs ·
            {{ getTimelineRowsForResources(slotGroup.resources).length }}
            resource{{
              getTimelineRowsForResources(slotGroup.resources).length === 1
                ? ""
                : "s"
            }}
          </div>
        </div>

        <div class="planner-timeline-cell">
          <div class="planner-grid-backdrop"></div>

          <template v-if="getTimelineRowsForResources(slotGroup.resources).length">
            <div
              v-for="(group, resourceIndex) in getTimelineRowsForResources(slotGroup.resources)"
              :key="`planner-resource-${slotGroup.key}-${group.key}-${resourceIndex}`"
              class="planner-track"
            >
              <div
                class="planner-bar"
                :class="getTimelineToneClass(group.resource)"
                :style="getTimelineBarStyle(slotGroup)"
              >
                <span class="planner-bar-title">
                  {{ getTimelineResourceLabel(group) }}
                </span>
                <span class="planner-bar-meta">
                  {{ getTimelineResourceMeta(group) }}
                </span>
              </div>
            </div>
          </template>

          <div v-else class="planner-empty-state">
            No resources scheduled for this date.
          </div>
        </div>
      </div>
    </div>

    <div v-else class="planner-empty-board">
      Add resources to see the timeline view.
    </div>
  </div>
</template>

<style scoped>
.planner-shell {
  --planner-hour-count: 24;
  --planner-hour-width: 30px;
  --planner-timeline-min-width: calc(var(--planner-hour-width) * var(--planner-hour-count));
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow: 0 18px 45px -28px rgba(15, 23, 42, 0.35);
}

.planner-header-row {
  display: grid;
  grid-template-columns: 180px minmax(var(--planner-timeline-min-width), 1fr);
  border-bottom: 1px solid rgb(226 232 240);
  background: linear-gradient(180deg, rgba(241, 245, 249, 0.96) 0%, rgba(248, 250, 252, 0.92) 100%);
}

.planner-date-header,
.planner-date-cell {
  padding: 1rem;
  border-right: 1px solid rgb(226 232 240);
}

.planner-date-header {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

.planner-hours-header {
  display: grid;
  grid-template-columns: repeat(var(--planner-hour-count), minmax(0, 1fr));
  min-width: var(--planner-timeline-min-width);
}

.planner-hour-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  border-left: 1px solid rgb(226 232 240);
  font-size: 0.72rem;
  font-weight: 700;
  color: rgb(100 116 139);
}

.planner-entry-row {
  display: grid;
  grid-template-columns: 180px minmax(var(--planner-timeline-min-width), 1fr);
  border-bottom: 1px solid rgb(226 232 240);
}

.planner-entry-row:last-child {
  border-bottom: 0;
}

.planner-date-cell {
  background: rgba(248, 250, 252, 0.88);
}

.planner-date-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(15 23 42);
}

.planner-date-subtitle,
.planner-date-meta {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: rgb(100 116 139);
}

.planner-timeline-cell {
  position: relative;
  min-width: var(--planner-timeline-min-width);
  padding: 0.8rem 0;
}

.planner-grid-backdrop {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    to right,
    transparent,
    transparent calc((100% / var(--planner-hour-count)) - 1px),
    rgba(226, 232, 240, 0.9) calc((100% / var(--planner-hour-count)) - 1px),
    rgba(226, 232, 240, 0.9) calc(100% / var(--planner-hour-count))
  );
  pointer-events: none;
}

.planner-track {
  position: relative;
  height: 2.4rem;
  margin: 0.35rem 0.75rem;
}

.planner-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 5rem;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  overflow: hidden;
  box-shadow: 0 10px 18px -14px rgba(15, 23, 42, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
}

.planner-bar-title,
.planner-bar-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planner-bar-title {
  font-size: 0.74rem;
  font-weight: 700;
}

.planner-bar-meta {
  font-size: 0.64rem;
  opacity: 0.92;
}

.planner-bar--emerald {
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
}

.planner-bar--rose {
  background: linear-gradient(90deg, #f43f5e 0%, #e11d48 100%);
}

.planner-bar--violet {
  background: linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%);
}

.planner-bar--cyan {
  background: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
}

.planner-bar--amber {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.planner-bar--fuchsia {
  background: linear-gradient(90deg, #d946ef 0%, #c026d3 100%);
}

.planner-empty-state,
.planner-empty-board {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 4.5rem;
  padding: 1rem;
  font-size: 0.85rem;
  color: rgb(100 116 139);
}

.planner-empty-board {
  border-top: 1px solid rgb(226 232 240);
}

@media (prefers-color-scheme: dark) {
  .planner-shell {
    border-color: rgb(51 65 85);
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.98) 100%);
    box-shadow: 0 18px 45px -28px rgba(2, 6, 23, 0.9);
  }

  .planner-header-row {
    border-bottom-color: rgb(51 65 85);
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.92) 100%);
  }

  .planner-date-header,
  .planner-date-cell {
    border-right-color: rgb(51 65 85);
  }

  .planner-date-header,
  .planner-hour-cell,
  .planner-date-subtitle,
  .planner-date-meta,
  .planner-empty-state,
  .planner-empty-board {
    color: rgb(203 213 225);
  }

  .planner-hour-cell {
    border-left-color: rgb(51 65 85);
  }

  .planner-entry-row {
    border-bottom-color: rgb(51 65 85);
  }

  .planner-date-cell {
    background: rgba(15, 23, 42, 0.72);
  }

  .planner-date-title {
    color: rgb(241 245 249);
  }

  .planner-grid-backdrop {
    background-image: repeating-linear-gradient(
      to right,
      transparent,
      transparent calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.7) calc((100% / var(--planner-hour-count)) - 1px),
      rgba(71, 85, 105, 0.7) calc(100% / var(--planner-hour-count))
    );
  }

  .planner-empty-board {
    border-top-color: rgb(51 65 85);
  }
}

@media (max-width: 900px) {
  .planner-header-row,
  .planner-entry-row {
    grid-template-columns: 140px minmax(var(--planner-timeline-min-width), 1fr);
  }

  .planner-date-header,
  .planner-date-cell {
    padding: 0.75rem;
  }
}
</style>
